/**
 * safety_certs — every vertical. Crew certs (GET /safety/team-cert-summary)
 * and open safety work (GET /safety/dashboard).
 *
 * Two reads, two failure states: one failing does not blank the other. An
 * absent count is "—", never 0 — "0 expired" is a clean bill of health and
 * must only ever be the API's answer.
 *
 * EXPIRED IS THE ONE ALERT FIGURE. An expired cert means someone cannot be
 * sent out today; everything else on the card is a fact (Expiring is a renewal
 * to book). So Expired alone gets the red badge label, the same treatment as
 * Overdue on Receivables, and only while the API says the count is above zero.
 * At zero, or when the count did not arrive, the label is plain: an absent
 * count is not a problem we know about, and a red badge over "—" would claim one.
 */
import { displayValue } from '../../../features/fieldiq-handoff/contracts';
import { queryLoadFailure } from '../../../lib/load-failure';
import { LoadFailureNotice } from '../../ui/LoadFailureNotice';
import { WidgetFigure, WidgetPanel, WidgetPending } from '../WidgetPanel';
import { parseCount } from '../buildActionAlerts';
import { useOverviewSafetyDash, useOverviewTeamCertSummary } from '../overviewQueries';

export default function SafetyCertsWidget() {
  const certs = useOverviewTeamCertSummary();
  const safety = useOverviewSafetyDash();
  const certFailure = certs.data === undefined ? queryLoadFailure(certs) : null;
  const safetyFailure = safety.data === undefined ? queryLoadFailure(safety) : null;
  const n = (v: unknown) => displayValue(parseCount(v));
  const expired = certs.data ? parseCount(certs.data.expired) : null;

  return (
    <WidgetPanel widgetKey="safety_certs" eyebrow="People and records" title="Safety & Certs" link={{ label: 'Safety', to: '/safety' }}>
      {/* Two reads, two groups. With no tile edges to tell them apart, a rule
          between them does, and the incidents row keeps the certs row's three
          columns so its figures sit under the ones above. */}
      <div className="divide-y divide-fieldiq-border px-4">
        <div className="py-4">
          {certFailure ? (
            <LoadFailureNotice error={null} kind={certFailure} subject="team certs" className="py-4" />
          ) : !certs.data ? (
            <WidgetPending rows={1} />
          ) : (
            <div className="grid grid-cols-3 gap-x-4 gap-y-5">
              <WidgetFigure testId="certs-expired" label="Expired" alert={expired !== null && expired > 0} value={n(certs.data.expired)} />
              <WidgetFigure testId="certs-expiring" label="Expiring" value={n(certs.data.expiringSoon)} sub="30 days" />
              <WidgetFigure testId="certs-current" label="Current" value={n(certs.data.current)} />
            </div>
          )}
        </div>
        <div className="py-4">
          {safetyFailure ? (
            <LoadFailureNotice error={null} kind={safetyFailure} subject="safety records" className="py-4" />
          ) : !safety.data ? (
            <WidgetPending rows={1} />
          ) : (
            <div className="grid grid-cols-3 gap-x-4 gap-y-5">
              <WidgetFigure testId="safety-incidents" label="Open incidents" value={n(safety.data.openIncidents)} />
              <WidgetFigure testId="safety-jsas" label="JSAs to approve" value={n(safety.data.pendingJsaApprovals)} />
            </div>
          )}
        </div>
      </div>
    </WidgetPanel>
  );
}
